import { useState } from "react";
import { X, Loader2, Send, Check, AlertTriangle, LifeBuoy } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SUPPORT_INBOX = "shiftlogsupport@gmail.com";

/**
 * SupportModal — one-tap "Technical Support" ticket. Structured email is
 * fired to shiftlogsupport@gmail.com with the user's role/site/crew already
 * appended so triage doesn't need to chase up context.
 */
export default function SupportModal({ open, onClose }) {
  const { user } = useAuth();
  const [category, setCategory] = useState("bug");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    const body = message.trim();
    if (body.length < 8) {
      setMsg({ type: "err", text: "Add a couple of sentences so we can triage this properly." });
      return;
    }
    setBusy(true); setMsg(null);
    const userName = user?.name || user?.username || "Unknown user";
    const siteName = user?.site_code || "no-site";
    const subject = `[Pit Link Support] — ${userName} — ${siteName}`;
    const structured = [
      `Category: ${category}`,
      `User: ${userName} (${user?.username || "?"})`,
      `Role: ${user?.role || "worker"}${user?.is_admin ? " · admin" : ""}`,
      `Crew: ${user?.crew_name || user?.crew_id || "—"}`,
      `Site: ${siteName}`,
      `Trade: ${user?.trade || "—"}`,
      `Email: ${user?.email || "—"}`,
      `User Agent: ${typeof navigator !== "undefined" ? navigator.userAgent : "n/a"}`,
      `URL: ${typeof window !== "undefined" ? window.location.href : "n/a"}`,
      `When: ${new Date().toISOString()}`,
      "",
      "— Issue —",
      body,
    ].join("\n");
    const html = `<div style="font-family:Arial,sans-serif;font-size:13px;color:#111;line-height:1.5"><pre style="white-space:pre-wrap;font:inherit;margin:0">${escapeHtml(structured)}</pre></div>`;

    try {
      // Reuse the same /email/send-document endpoint the rest of the portal
      // uses for report emails. No PDF attachment for support tickets — we
      // just want the plain-text triage body in Josh's inbox.
      await api.post("/email/send-document", {
        kind: "support",
        to: SUPPORT_INBOX,
        recipient: SUPPORT_INBOX,
        email: SUPPORT_INBOX,
        subject,
        html,
        message: structured,
        attach_pdf: false,
      });
      setMsg({ type: "ok", text: "Sent — support will follow up on the email you have on file." });
      setMessage("");
      setTimeout(onClose, 1200);
    } catch (ex) {
      setMsg({ type: "err", text: apiError(ex) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="support-modal">
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }} onClick={onClose} />
      <div className="relative panel-heavy w-full max-w-lg">
        <form onSubmit={submit}>
          <div className="flex items-center gap-3 p-5" style={{ borderBottom: "1px solid var(--border)" }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                 style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
              <LifeBuoy className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-muted">Technical Support</div>
              <div className="font-display font-black text-xl tracking-tight">Report an issue</div>
            </div>
            <button type="button" className="btn btn-sm btn-ghost !px-2" onClick={onClose} data-testid="support-close"><X className="w-4 h-4" /></button>
          </div>

          <div className="p-5 space-y-3">
            <div className="field">
              <label className="text-[10px]">Category</label>
              <div className="grid grid-cols-4 gap-2" data-testid="support-category">
                {["bug", "data", "how-to", "other"].map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCategory(c)}
                    className="rounded-xl py-2 text-xs font-black uppercase tracking-widest transition-all"
                    style={{
                      background: category === c ? "rgba(245,130,32,0.18)" : "rgba(255,255,255,0.03)",
                      color: category === c ? "var(--primary)" : "rgba(255,255,255,0.6)",
                      border: `1px solid ${category === c ? "var(--primary)" : "rgba(255,255,255,0.08)"}`,
                    }}
                    data-testid={`support-category-${c}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <div className="field">
              <label className="text-[10px]">What&apos;s going wrong?</label>
              <textarea
                rows={5}
                className="textarea"
                required
                autoFocus
                placeholder="Describe the issue — where you were, what you tried, what happened vs expected."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                data-testid="support-message"
              />
              <div className="text-[10px] text-muted mt-1">
                We attach your role, site, crew, browser, and current page automatically — no need to re-type it.
              </div>
            </div>
            {msg && (
              <div className={`pill ${msg.type === "ok" ? "pill-green" : "pill-red"} !text-xs !py-2 !px-3 justify-start`} data-testid="support-msg">
                {msg.type === "ok" ? <Check className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />} {msg.text}
              </div>
            )}
          </div>
          <div className="p-4 flex justify-end gap-2" style={{ borderTop: "1px solid var(--border)" }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy || !message.trim()} data-testid="support-send">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send to Support
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function escapeHtml(s) {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
